import {
    uploadTitle,
    uploadActionButton,
    changeLocationButton,
} from './AttachmentFolderPickerModal.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { AttachmentFolderPickerProperties } from '../types/AttachmentFolderPickerProperties';
import { AttachmentFolderPickerResult } from '../types/AttachmentFolderPickerResult';
import { AttachmentFolderSelectorBottomPart } from './AttachmentFolderSelectorBottomPart';
import { ControlIcons } from 'owa-control-icons';
import { getAttachmentFolderPickerResult } from '../selectors/folderPickerResultSelectors';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { isAttachmentFolderSelectorOpen } from '../selectors/attachmentFolderSelectorModalSelectors';
import { isChangeLocationAllowedForProvider } from '../selectors/fileProviderSelectors';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { logUsage } from 'owa-analytics';
import { Modal } from 'office-ui-fabric-react/lib/Modal';
import { observer } from 'mobx-react';
import { ProviderFolderView } from './ProviderFolderView';
import { showFileProviderFolderSelector } from 'owa-fileprovider-folder-selector/lib/lazyIndex';
import {
    closeAttachmentFolderPicker,
    locationChanged,
    openAttachmentFolderPicker,
} from '../actions/internalActions';

const styles = require('./AttachmentFolderPickerModal.scss');

interface AttachmentFolderPickerModalProps {
    onFolderSelectedCallback: (result: AttachmentFolderPickerResult) => void;
    onErrorCallback: (error: Error) => void;
}

const enum UserAction {
    Upload,
    ChangeLocation,
    Dismiss,
}

function getPrimaryButtonString() {
    return loc(uploadActionButton);
}

@observer
class AttachmentFolderPickerModal extends React.Component<AttachmentFolderPickerModalProps, {}> {
    private userAction: UserAction | null = null;

    render() {
        return (
            <Modal
                isOpen={isAttachmentFolderSelectorOpen()}
                onDismiss={this.dismissModal}
                onDismissed={this.onModalDismissed}>
                {this.renderFolderSelector()}
            </Modal>
        );
    }

    private renderFolderSelector() {
        return (
            <div className={styles.modalContainer}>
                <Icon
                    className={styles.closeIcon}
                    iconName={ControlIcons.Cancel}
                    onClick={this.dismissModal}
                />
                <div className={styles.headerContainer}>{loc(uploadTitle)}</div>
                <div className={styles.currentFolderContainer}>
                    <div className={styles.currentFolderInnerContainer}>
                        <ProviderFolderView />
                    </div>
                </div>
                <div className={styles.changeLocationContainer}>{this.renderChangeLocation()}</div>
                <div className={styles.bottomContainer}>
                    <AttachmentFolderSelectorBottomPart
                        onPrimaryButtonClicked={this.primaryButtonClicked}
                        onCancelButtonClicked={this.dismissModal}
                        primaryButtonString={getPrimaryButtonString}
                    />
                </div>
            </div>
        );
    }

    private renderChangeLocation() {
        return isChangeLocationAllowedForProvider() ? (
            <div className={styles.changeLocation}>
                <Link onClick={this.changeLocationClicked}>{loc(changeLocationButton)}</Link>
            </div>
        ) : (
            <div className={styles.emptyChangeLocation} />
        );
    }

    private onModalDismissed = () => {
        if (this.userAction === UserAction.Upload) {
            logUsage('AttFolderPickerUpload');
            this.triggerFolderSelectedCallback();
        } else if (this.userAction === UserAction.Dismiss) {
            logUsage('AttFolderPickerDismissed');
            this.props.onFolderSelectedCallback({ isFolderSelected: false });
        }

        this.userAction = null;
    };

    private primaryButtonClicked = () => {
        this.userAction = UserAction.Upload;
        closeAttachmentFolderPicker();
    };

    private dismissModal = () => {
        this.userAction = UserAction.Dismiss;
        closeAttachmentFolderPicker();
    };

    private changeLocationClicked = async () => {
        logUsage('AttFolderPickerChangeLocation');
        const { onErrorCallback } = this.props;
        try {
            // When we open the folder selector we close the current modal
            // so user can take action from the provider folder selector
            const folderSelectorPromise = showFileProviderFolderSelector({
                primaryButtonString: () => loc(uploadActionButton),
                titleString: () => loc(uploadTitle),
            });

            this.userAction = UserAction.ChangeLocation;
            closeAttachmentFolderPicker();

            // We await the promise here instead of previously so that we can open
            // the provider folder selector, close the current folder picker and then
            // wait for any result
            const result = await folderSelectorPromise;

            if (result.isFolderSelected) {
                locationChanged(result);
                this.triggerFolderSelectedCallback();
            } else {
                this.props.onFolderSelectedCallback({
                    isFolderSelected: false,
                });
            }
        } catch (error) {
            onErrorCallback(error);
        }
    };

    private triggerFolderSelectedCallback() {
        const result = getAttachmentFolderPickerResult();
        if (result) {
            this.props.onFolderSelectedCallback(result);
        } else {
            // We should generally not reach this state as among the selected folder
            // the default folder should be there
            this.props.onErrorCallback(
                new Error(
                    'Not able to figure out any folder that user has selected for the primary action'
                )
            );
        }
    }
}

export const ATTACHMENT_FOLDER_PICKER_DIV_ID = 'attachmentFolderPicker';
export function showAttachmentFolderPicker(
    properties: AttachmentFolderPickerProperties
): Promise<AttachmentFolderPickerResult> {
    let folderSelectorDiv = document.getElementById(ATTACHMENT_FOLDER_PICKER_DIV_ID);

    if (!folderSelectorDiv) {
        folderSelectorDiv = document.createElement('div');
        folderSelectorDiv.id = ATTACHMENT_FOLDER_PICKER_DIV_ID;
        document.body.appendChild(folderSelectorDiv);
    }

    /* tslint:disable:promise-must-complete */
    return new Promise<AttachmentFolderPickerResult>((resolve, reject) => {
        ReactDOM.render(
            <AttachmentFolderPickerModal
                onFolderSelectedCallback={resolve}
                onErrorCallback={reject}
            />,
            folderSelectorDiv
        );

        openAttachmentFolderPicker(properties);
    });
    /* tslint:enable:promise-must-complete */
}
